import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, MessageCircle, Pencil, Trash2, PenLine, CheckCircle2, Clock } from "lucide-react";
import { Empty, ConfirmDelete } from "@/components/Bits";
import { EditPagDialog, ReciboSignDialog, sendRecibo } from "@/components/PagamentoDialogs";
import { useAuth } from "@/context/AuthContext";
import { useFetch, fmtDate, openPdf, BRL } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const iconBtn = "h-11 w-11 rounded-xl border border-[#E2DDD3] flex items-center justify-center hover:border-[#1E4D2B] shrink-0";

export const PagamentosList = ({ tecnicoId, version, onChange }) => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const staff = isAdmin || user.role === "financeiro";
  const [params, setParams] = useSearchParams();
  const { data, reload } = useFetch("/pagamentos", { tecnico_id: tecnicoId || undefined, v: version });
  const [edit, setEdit] = useState(null);
  const [sign, setSign] = useState(null);
  const [del, setDel] = useState(null);
  useEffect(() => {
    const pid = params.get("recibo");
    const p = pid && (data || []).find((x) => x.pagamento_id === pid);
    if (p && !p.assinatura_tecnico && !staff) setSign(p);
  }, [params, data, staff]);
  const done = () => { reload(); onChange?.(); if (params.get("recibo")) setParams({}, { replace: true }); };
  const remove = async () => {
    try { await api.delete(`/pagamentos/${del.pagamento_id}`); toast.success("Pagamento excluído; itens voltaram a pendentes"); setDel(null); done(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  if (!data.length) return <Empty testId="pag-empty">Nenhum pagamento registrado.</Empty>;
  return (
    <div className="space-y-2" data-testid="pag-list">
      {data.map((p) => (
        <div key={p.pagamento_id} className="bg-white rounded-2xl border border-[#E2DDD3] p-4 flex flex-wrap items-center gap-3" data-testid={`pag-item-${p.pagamento_id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{p.numero}{staff && ` · ${p.tecnico_nome}`}</p>
            <p className="text-xs text-[#5C6B66]">{fmtDate(p.data)} · {p.itens.length} item(ns){p.observacao ? ` · ${p.observacao}` : ""}</p>
          </div>
          <p className="font-bold text-[#1E4D2B]">{BRL(p.valor)}</p>
          <span className={`h-9 px-3 rounded-full text-xs font-bold flex items-center gap-1 ${p.assinatura_tecnico ? "bg-[#E8F5E9] text-[#1E4D2B]" : "bg-[#FEF3C7] text-[#92400E]"}`} data-testid={`pag-sign-status-${p.pagamento_id}`}>
            {p.assinatura_tecnico ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}{p.assinatura_tecnico ? "Assinado" : "Aguardando assinatura"}
          </span>
          <div className="flex gap-2">
            <button className={iconBtn} aria-label="Baixar recibo" onClick={() => openPdf(`/pagamentos/${p.pagamento_id}/recibo`, `${p.numero}.pdf`).catch(() => toast.error("Falha ao gerar PDF"))} data-testid={`pag-pdf-${p.pagamento_id}`}><FileDown className="h-5 w-5" /></button>
            {!staff && !p.assinatura_tecnico && <button onClick={() => setSign(p)} className="h-11 px-4 rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center gap-2" data-testid={`pag-sign-${p.pagamento_id}`}><PenLine className="h-4 w-4" /> Assinar</button>}
            {staff && <button className={`${iconBtn} text-[#25D366]`} aria-label="Enviar no WhatsApp" onClick={() => sendRecibo(p.pagamento_id)} data-testid={`pag-wpp-${p.pagamento_id}`}><MessageCircle className="h-5 w-5" /></button>}
            {isAdmin && <>
              <button className={iconBtn} aria-label="Editar" onClick={() => setEdit(p)} data-testid={`pag-edit-${p.pagamento_id}`}><Pencil className="h-5 w-5" /></button>
              <button className={`${iconBtn} text-[#B05A40]`} aria-label="Excluir" onClick={() => setDel(p)} data-testid={`pag-delete-${p.pagamento_id}`}><Trash2 className="h-5 w-5" /></button>
            </>}
          </div>
        </div>
      ))}
      <EditPagDialog pag={edit} onOpenChange={(o) => !o && setEdit(null)} onSaved={done} />
      <ReciboSignDialog pag={sign} onOpenChange={(o) => !o && setSign(null)} onSaved={done} defaultName={user.name} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Excluir ${del?.numero || "pagamento"}?`}
        description="As comissões e bônus deste recibo voltarão a ficar pendentes." onConfirm={remove} />
    </div>
  );
};
